import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import CleanupCarousel from "@/components/CleanupCarousel";
import { SERVICES, SUBURBS, BUSINESS } from "@/lib/constants";
import { ArrowRight, Camera, MapPin, Users, Truck, Trees, HardHat, Clock, Mail } from "lucide-react";

const steps = [
  { n: "01", title: "Send your site details", body: "Fill in the short form: address, suburb, waste type and your preferred timing. Takes 2–3 minutes." },
  { n: "02", title: "Add a few photos", body: "Photos of the site let us assess volume, waste type and access — so the quote back to you is accurate, not a guess." },
  { n: "03", title: "You'll hear back within 1 business day", body: "We review your enquiry and match it with one suitable local contractor covering your suburb. You'll receive their quote by phone or email." },
  { n: "04", title: "You decide — no obligation", body: "Approve the quote and confirm the clean-up directly with the contractor, or walk away. It's your call, no charge either way." },
];

const audiences = [
  { icon: HardHat, title: "Builders & supervisors", body: "One-off tidy-ups, ongoing site sweeps and end-of-build handover clean-ups." },
  { icon: MapPin, title: "Land buyers & new home owners", body: "Get your block cleared and ready for the builder to start on schedule." },
  { icon: Users, title: "Developers & property managers", body: "Vacant lot clean-ups, illegal dumping removal and pre-listing site tidy-ups." },
];

const commonJobs = [
  "Vacant lot clean-up before builder start", "Timber offcuts, plasterboard and bricks",
  "Green waste, packaging and cartons", "Dumped rubbish on vacant land",
  "End-of-build tidy for handover", "General construction debris removal",
];

const faqs = [
  { q: "How much does a site clean-up cost?", a: "There's no fixed price — it depends on the volume of waste, waste type, site access, labour time, truck size and tip fees. Every quote you receive through us breaks these down so you know exactly what you're paying for. Requesting a quote is free." },
  { q: "Is the quote free? Do I have to accept it?", a: "Yes, quote requests are 100% free and there's no obligation. If the quote doesn't suit, you can walk away — no charge, no follow-up pressure." },
  { q: "How quickly will I hear back?", a: "We aim to respond to enquiries within 1 business day. If your job is urgent, please tick 'ASAP' on the form and call us as well — priority enquiries are actioned faster subject to local contractor availability." },
  { q: "Can you clean a block before my builder starts?", a: "Yes. Pre-build vacant lot clean-ups are one of our most common enquiries in Epping, Wollert, Craigieburn, Mickleham and Donnybrook. Send photos and your builder's start date and we'll turn the quote around quickly." },
  { q: "Do you handle asbestos or contaminated soil?", a: "No — asbestos, chemicals and contaminated soil require appropriately qualified and licensed specialists. If you suspect hazardous materials, please disclose this on the form and we can refer suitable specialists where available." },
  { q: "Will my details be shared with lots of contractors?", a: "No. If you go ahead, your enquiry is passed to one matched local contractor who covers your suburb. We don't sell your details, and we don't send your enquiry to a bidding pool." },
  { q: "Do you claim to be the cheapest or offer same-day service?", a: "No, we don't. We focus on matching you with contractors who understand construction work and can quote transparently. Same-day service depends entirely on local contractor availability — we won't promise something we can't deliver." },
];

export default function Home() {
  return (
    <PublicLayout>
      <Seo
        title="Site Cleanup | Site clean-up quotes for builders, land owners & developers"
        description="Request a site clean-up quote across Melbourne and Victoria. Vacant lot clean-ups, builder waste removal, pre-build site clearing and end-of-build tidy-ups. Upload photos, get matched with a local crew."
      />

      {/* HERO */}
      <section className="relative rs-hero-gradient text-white overflow-hidden" data-testid="home-hero">
        <div className="rs-container py-24 md:py-36 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <span className="rs-eyebrow text-safety">Melbourne · Victoria · Australia</span>
            <h1 className="mt-4 text-5xl md:text-7xl lg:text-8xl uppercase font-display leading-[0.95] tracking-tighter">
              Site Cleanup Quotes<br/> Across <span className="text-safety">Melbourne</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80">
              Vacant lot clean-up, builder waste removal and pre-build site preparation. Send us a few photos and your site address — we'll organise a free, no-obligation quote and match you with a local crew across Epping, Craigieburn, Wollert, Donnybrook and beyond.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 text-white/85"><span className="w-1.5 h-1.5 bg-safety rounded-full" /> Free quote</span>
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 text-white/85"><span className="w-1.5 h-1.5 bg-safety rounded-full" /> No obligation</span>
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 text-white/85"><span className="w-1.5 h-1.5 bg-safety rounded-full" /> Reply within 1 business day</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/request-quote" className="rs-btn-primary" data-testid="hero-cta-primary">
                Get my free quote <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link to="/partner" className="rs-btn-outline-light" data-testid="hero-cta-partner">Partner With Us</Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest text-white/60">
              <span className="flex items-center gap-2"><Camera size={14}/> Photo-based quotes</span>
              <span className="flex items-center gap-2"><MapPin size={14}/> Local contractor matching</span>
              <span className="flex items-center gap-2"><Clock size={14}/> Built for construction timelines</span>
            </div>
          </div>
          <div className="md:col-span-4 border-l-4 border-safety pl-6 py-2">
            <p className="rs-eyebrow text-white/80">Prefer email?</p>
            <p className="font-display text-3xl md:text-4xl mt-1">Email the team</p>
            <a href={`mailto:${BUSINESS.email}`} className="mt-2 flex items-center gap-2 text-safety hover:text-white font-semibold text-base break-all" data-testid="hero-email">
              <Mail size={18}/> {BUSINESS.email}
            </a>
            <p className="mt-4 text-xs text-white/60">Or send photos through the quote form — we'll review, work up a quote and email you back within 1 business day with a matched local contractor.</p>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-white border-b border-border" data-testid="home-trust-bar">
        <div className="rs-container py-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { t: "Free & no obligation", b: "Requesting a quote through us costs nothing. If the quote doesn't suit, walk away — no charge, no chasing." },
            { t: "Transparent, itemised quotes", b: "Every quote breaks down labour, truck, tip fees and disposal — so you know exactly what you're paying for." },
            { t: "Local Melbourne focus", b: "Priority coverage across Epping, Wollert, Craigieburn, Mickleham, Donnybrook, Mernda, Tarneit, Clyde and Werribee." },
            { t: "One matched contractor", b: "Your details go to one suitable local contractor — not a bidding pool. We never sell your data." },
          ].map((x) => (
            <div key={x.t} className="border-t-2 border-safety pt-4">
              <p className="font-display uppercase text-lg text-asphalt leading-tight">{x.t}</p>
              <p className="mt-2 text-sm text-muted-foreground">{x.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MACHINERY SHOWCASE */}
      <section className="relative bg-asphalt text-white overflow-hidden" data-testid="home-machinery-section">
        <div className="absolute inset-0 rs-diag opacity-25" />
        <div className="rs-container relative py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <span className="rs-eyebrow text-safety">Machinery in motion</span>
              <h2 className="mt-3 text-4xl md:text-6xl uppercase leading-[0.95]">The right kit for the job.</h2>
              <p className="mt-4 text-white/75 max-w-xl">Excavators, dump trucks and skip bins — matched to the site, the waste type and the timeline. We coordinate the machinery so the block is cleared, hauled and ready for the next stage.</p>
            </div>
            <div className="md:col-span-5 md:justify-self-end">
              <Link to="/request-quote" className="rs-btn-primary" data-testid="machinery-cta">Request a machinery quote <ArrowRight size={16} className="ml-2" /></Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                src: "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?auto=format&fit=crop&w=1000&q=80",
                tag: "01 · Excavators",
                title: "Land clearing & heavy debris",
                body: "Vegetation, dumped rubbish, buried debris and controlled site excavation.",
              },
              {
                src: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1000&q=80",
                tag: "02 · Dump & tip trucks",
                title: "Haulage to licensed facilities",
                body: "Mixed loads collected and taken to responsible disposal points.",
              },
              {
                src: "https://images.unsplash.com/photo-1590496793929-36417d3117de?auto=format&fit=crop&w=1000&q=80",
                tag: "03 · Skip bins",
                title: "On-site staged bins",
                body: "For active builds — bins swapped out as they fill with offcuts and rubbish.",
              },
            ].map((m) => (
              <div key={m.tag} className="relative border border-white/10 overflow-hidden h-[360px] group" data-testid={`machinery-card-${m.tag.split('·')[0].trim()}`}>
                <img src={m.src} alt={m.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/50 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <span className="self-start bg-safety text-white text-[11px] font-bold uppercase tracking-[0.2em] px-2.5 py-1">{m.tag}</span>
                  <div>
                    <h3 className="font-display text-white text-2xl uppercase leading-tight">{m.title}</h3>
                    <p className="text-white/80 text-sm mt-2 max-w-xs">{m.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLEANUP CAROUSEL */}
      <CleanupCarousel />

      {/* SERVICES BENTO */}
      <section className="py-24" data-testid="home-services">
        <div className="rs-container">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="rs-eyebrow">Services</span>
              <h2 className="mt-3 text-4xl md:text-5xl uppercase">Common jobs we quote on</h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-safety hover:underline underline-offset-4">All services →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="rs-card md:col-span-7 md:row-span-2 p-8 flex flex-col justify-between min-h-[320px]" style={{backgroundImage: 'linear-gradient(135deg, rgba(33,37,41,0.85), rgba(33,37,41,0.65)), url(https://images.unsplash.com/photo-1575281923032-f40d94ef6160?auto=format&fit=crop&w=1200&q=80)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
              <span className="rs-eyebrow text-safety">Feature service</span>
              <div>
                <h3 className="font-display text-white text-4xl uppercase leading-tight">Builder waste removal, done properly.</h3>
                <p className="text-white/80 mt-3 max-w-md">Timber offcuts, plasterboard, bricks, tiles, straps, cartons, plastic wrap and general site rubbish — cleared and hauled by crews who understand construction sites.</p>
                <Link to="/request-quote" className="mt-6 inline-flex items-center gap-2 text-safety font-semibold">Request a quote <ArrowRight size={16}/></Link>
              </div>
            </div>
            {SERVICES.slice(0, 4).map((s, i) => (
              <div key={s.slug} className={`rs-card ${i === 0 ? "md:col-span-5" : "md:col-span-5"}`} data-testid={`service-card-${s.slug}`}>
                <div className="flex items-center gap-3 mb-3">
                  {[Truck, Trees, HardHat, Camera][i]  && (() => {
                    const Ic = [Truck, Trees, HardHat, Camera][i];
                    return <Ic size={22} className="text-safety" />;
                  })()}
                  <h3 className="font-display uppercase text-xl">{s.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{s.blurb}</p>
              </div>
            )).slice(1)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white border-y border-border">
        <div className="rs-container">
          <span className="rs-eyebrow">How it works</span>
          <h2 className="mt-3 text-4xl md:text-5xl uppercase max-w-2xl">A simple, structured process — from enquiry to clean site.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="border-t-2 border-safety pt-6" data-testid={`step-${s.n}`}>
                <div className="font-display text-5xl text-safety leading-none">{s.n}</div>
                <h3 className="mt-4 font-display text-xl uppercase">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="py-24">
        <div className="rs-container grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="rs-eyebrow">Who we help</span>
            <h2 className="mt-3 text-4xl md:text-5xl uppercase">Built for people who need the site clean, safe and ready for the next stage.</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">Most clean-up jobs happen under pressure — the builder needs to start, the block needs to look presentable for the buyer, or the site needs a final tidy before handover. Our enquiry process is designed for exactly that.</p>
            <Link to="/request-quote" className="rs-btn-primary mt-8">Get my free quote</Link>
          </div>
          <div className="grid gap-4">
            {audiences.map(({ icon: Ic, title, body }) => (
              <div key={title} className="rs-card flex gap-4 items-start">
                <span className="w-10 h-10 grid place-items-center bg-safety/10 text-safety shrink-0"><Ic size={20}/></span>
                <div>
                  <h3 className="font-display uppercase text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON JOBS */}
      <section className="py-24 bg-asphalt text-white relative overflow-hidden">
        <div className="absolute inset-0 rs-diag opacity-40" />
        <div className="rs-container relative">
          <span className="rs-eyebrow text-safety">Common jobs</span>
          <h2 className="mt-3 text-4xl md:text-5xl uppercase text-white max-w-3xl">If you can see it on your site, we can quote on it.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {commonJobs.map((c, i) => (
              <div key={c} className="border border-white/15 p-5 flex items-baseline gap-3">
                <span className="font-display text-2xl text-safety">{String(i+1).padStart(2,'0')}</span>
                <span className="font-semibold text-white/90">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-24">
        <div className="rs-container grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="rs-eyebrow">Service areas</span>
            <h2 className="mt-3 text-4xl md:text-5xl uppercase">Starting in Melbourne & Victoria — expanding across Australia.</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">We prioritise the northern and western Melbourne growth corridors where new builds, land estates and construction activity are concentrated. Enquiries from other states are welcome and matched where possible.</p>
            <Link to="/service-areas" className="rs-btn-outline mt-8">Browse all service areas</Link>
          </div>
          <div className="border border-border p-6 bg-white">
            <p className="rs-eyebrow">Priority suburbs</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SUBURBS.map((s) => (
                <Link key={s.slug} to={`/service-areas/${s.slug}`} data-testid={`suburb-tag-${s.slug}`} className="px-3 py-1.5 border border-border text-sm hover:border-safety hover:text-safety transition-colors">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PARTNER CTA */}
      <section className="py-20 bg-safety text-white relative overflow-hidden">
        <div className="rs-container grid md:grid-cols-2 gap-10 items-center relative">
          <div>
            <span className="rs-eyebrow text-white">For contractors</span>
            <h2 className="mt-3 text-4xl md:text-5xl uppercase leading-tight">Join our contractor network. Receive site clean-up leads.</h2>
            <p className="mt-4 text-white/90 max-w-lg">We connect Australian site clean-up contractors with real quote opportunities from builders, developers and land owners. Submit your details and we'll be in touch.</p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/partner" className="inline-flex items-center gap-2 bg-asphalt text-white px-8 py-4 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors" data-testid="home-partner-cta">
              Partner with us <ArrowRight size={18}/>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white border-t border-border">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">FAQ</span>
            <h2 className="mt-3 text-4xl md:text-5xl uppercase">Straight answers for site owners & builders.</h2>
          </div>
          <div className="md:col-span-2 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group border border-border bg-white p-5 open:border-safety" data-testid={`faq-${f.q.slice(0,10)}`}>
                <summary className="cursor-pointer font-display uppercase text-lg text-asphalt list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-safety group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-asphalt text-white">
        <div className="rs-container text-center max-w-3xl mx-auto">
          <span className="rs-eyebrow text-safety">Free quote · no obligation</span>
          <h2 className="mt-3 text-4xl md:text-6xl uppercase leading-tight">Ready to get your block cleared?</h2>
          <p className="mt-4 text-white/70">One short form. A few photos of the site. We'll come back with a written quote within 1 business day — and you decide from there. No charge to enquire, no pressure to accept.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/request-quote" className="rs-btn-primary" data-testid="final-cta">Get my free quote</Link>
            <a href={`mailto:${BUSINESS.email}`} className="rs-btn-outline-light">Or email us</a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
